import { Link } from 'react-router-dom';
import { FaArrowRight, FaMapMarkerAlt } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import projectGroups from '../data/projectGroups';

const featuredProjectSlugs = [
    'new-life-center-channel-letters',
    'tex-pro-electrical',
    'la-violeta-cuban'
];

const copy = {
    en: {
        eyebrow: 'REAL WORK FOR LOCAL BUSINESSES',
        title: 'Featured Projects',
        subtitle: 'Design, production and professional installation completed for businesses in Houston and surrounding areas.',
        location: 'Houston, Texas',
        viewProject: 'View project',
        viewAll: 'View all projects',
        similar: 'Need something similar?',
        quote: 'Get a free quote'
    },
    es: {
        eyebrow: 'TRABAJOS REALES PARA NEGOCIOS LOCALES',
        title: 'Proyectos destacados',
        subtitle: 'Diseño, producción e instalación profesional realizados para negocios de Houston y sus alrededores.',
        location: 'Houston, Texas',
        viewProject: 'Ver proyecto',
        viewAll: 'Ver todos los proyectos',
        similar: '¿Necesitas algo similar?',
        quote: 'Solicita una cotización gratis'
    }
};

const FeaturedProjects = () => {
    const { language } = useLanguage();
    const { isDark } = useTheme();
    const text = copy[language] || copy.en;
    const projects = featuredProjectSlugs
        .map((slug) => projectGroups.find((project) => project.slug === slug))
        .filter(Boolean);

    return (
        <section id="featured-projects" className={`py-20 px-4 ${isDark ? 'bg-gray-950' : 'bg-white'}`}>
            <div className="max-w-7xl mx-auto">
                <div className="text-center mb-12">
                    <p className="text-atlantis font-bold tracking-widest text-sm mb-3">{text.eyebrow}</p>
                    <h2 className={`text-4xl md:text-5xl font-bold mb-5 ${isDark ? 'text-white' : 'text-saratoga'}`}>
                        {text.title}
                    </h2>
                    <p className={`text-lg max-w-3xl mx-auto ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        {text.subtitle}
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
                    {projects.map((project) => (
                        <Link
                            to={`/portfolio/${project.slug}`}
                            key={project.slug}
                            className={`group overflow-hidden rounded-2xl border shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}
                            aria-label={`${text.viewProject}: ${project.title}`}
                        >
                            <div className="relative h-64 overflow-hidden bg-gray-200">
                                <img
                                    src={project.media[0].type === 'video' ? project.media[0].poster : project.media[0].src}
                                    alt={`${project.title} - ${text.location}`}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    loading="lazy"
                                    width="640"
                                    height="480"
                                />
                                <span className="absolute top-4 left-4 rounded-full bg-black/75 text-white text-xs font-semibold px-3 py-2">
                                    {project.media[0].type === 'video' ? 'Video' : language === 'es' ? 'Proyecto realizado' : 'Completed project'}
                                </span>
                            </div>
                            <div className="p-6">
                                <p className="flex items-center gap-2 text-atlantis text-sm font-semibold mb-2">
                                    <FaMapMarkerAlt aria-hidden="true" /> {text.location}
                                </p>
                                <p className="text-xs font-bold uppercase tracking-wide text-atlantis mb-2">{project.category[language] || project.category.en}</p>
                                <h3 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-saratoga'}`}>{project.title}</h3>
                                <p className={`text-sm leading-relaxed line-clamp-3 mb-5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{project.summary[language] || project.summary.en}</p>
                                <span className="inline-flex items-center gap-2 font-bold text-atlantis group-hover:gap-3 transition-all">
                                    {text.viewProject} <FaArrowRight aria-hidden="true" />
                                </span>
                            </div>
                        </Link>
                    ))}
                </div>

                <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Link to="/portfolio" className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-saratoga text-white font-bold hover:bg-marshland transition-colors">
                        {text.viewAll} <FaArrowRight aria-hidden="true" />
                    </Link>
                    <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-gradient-to-r from-atlantis to-green-yellow-primary text-black font-bold hover:shadow-lg transition-shadow">
                        <span>{text.similar}</span> {text.quote}
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default FeaturedProjects;
